import React, { useState, useEffect, useContext, useMemo } from "react";
import LayoutWrapper from "../../components/LayoutWrapper";
import HeaderBar from "../../components/HeaderBar";
import { FaPlus, FaSearch, FaFilter } from "react-icons/fa";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";

// Helper: format Date object to "YYYY-MM-DD" for input[type=date]
const toInputDate = (d) => d.toISOString().split("T")[0];

// Helper: safe number display
const showVal = (v) => (v === null || v === undefined || v === "" ? "—" : v);

const HsdHrUpdate = () => {
  const { isDark, skin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;
  const navigate = useNavigate();
  const [showFilters, setShowFilters] = useState(true);

  // ─────────────────────────────────────────────────────────────
  // LocalStorage values
  // ─────────────────────────────────────────────────────────────
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";
  const storedBranchId = localStorage.getItem("branchId") || "";
  const branch =
    localStorage.getItem("branchType") || localStorage.getItem("branch") || "";
  const userId = localStorage.getItem("userId") || "";

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": storedBranchId,
      "x-branch": branch,
      "x-userid": userId,
    }),
    [finYearId, finYear, storedBranchId, branch, userId]
  );

  // Default dates: from = today minus 6 months, to = today
  const [fromDate, setFromDate] = useState(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return toInputDate(d);
  });

  const [toDate, setToDate] = useState(() => toInputDate(new Date()));

  // Filter branchId state
  const [selectedBranchId, setSelectedBranchId] = useState(() => {
    return storedBranchId ? parseInt(storedBranchId, 10) : 8;
  });

  const [vehicleCode, setVehicleCode] = useState("");
  const [vehicleOptions, setVehicleOptions] = useState([]);
  const [data, setData] = useState([]);
  const [branchList, setBranchList] = useState([]);

  const BASE = "http://nissi-india.com/api/hsdhr";

  // Normalize row so UI works for both SP output and Inline SQL output
  const normalizedData = useMemo(() => {
    return (data || []).map((r) => {
      const preStartHr = r.fld_preStartingHr ?? r.fld_Pre_HSDStartingHr;
      const curStartHr = r.fld_curStartingHr ?? r.fld_Cur_HSDStartingHr;
      const preCloseHr = r.fld_preClosingHr ?? r.fld_Pre_HSDClosingHr;
      const curCloseHr = r.fld_curClosingHr ?? r.fld_Cur_HSDClosingHr;

      const preStartKm = r.fld_preStartingKM ?? r.fld_Pre_HSDStartingKM;
      const curStartKm = r.fld_curStartingKM ?? r.fld_Cur_HSDStartingKM;
      const preCloseKm = r.fld_preClosingKM ?? r.fld_Pre_HSDClosingKM;
      const curCloseKm = r.fld_curClosingKM ?? r.fld_Cur_HSDClosingKM;

      const qty = r.fld_Qty ?? r.fld_qty;

      const vehicleDisplay =
        (r.fld_vehiclecodename && r.fld_vehiclecodename.trim() !== ""
          ? r.fld_vehiclecodename
          : null) ||
        (r.fld_vehiclecode && r.fld_vehiclecode.trim() !== ""
          ? r.fld_vehiclecode
          : null) ||
        "—";

      return {
        ...r,
        _qty: qty,
        _vehicleDisplay: vehicleDisplay,
        _preStartHr: preStartHr,
        _curStartHr: curStartHr,
        _preCloseHr: preCloseHr,
        _curCloseHr: curCloseHr,
        _preStartKm: preStartKm,
        _curStartKm: curStartKm,
        _preCloseKm: preCloseKm,
        _curCloseKm: curCloseKm,
        _sidate: r.fld_SIDate ?? r.fld_siDate ?? "—",
        _updated: r.fld_Date ?? r.fld_date ?? "—",
        _user: r.fld_username ?? r.fld_userName ?? "—",
      };
    });
  }, [data]);

  // Load branch list + vehicle list
  useEffect(() => {
    axios
      .get(`${BASE}/hsd_dropdown?branchId=${selectedBranchId}`, {
        headers: commonHeaders,
      })
      .then((res) => {
        const branches = res.data.branches || [];
        setBranchList(
          branches.filter(
            (b) => b.fld_Branchid !== "" && b.fld_Branchid != null
          )
        );

        const vehicles = res.data.vehicleCodes || [];
        setVehicleOptions(
          vehicles.filter(
            (v) => v.fld_vehichlecode && v.fld_vehichlecode !== "Select"
          )
        );
      })
      .catch((err) => {
        console.error("Dropdown fetch failed", err);
        toast.error("Failed to load dropdown data");
      });
  }, [selectedBranchId, commonHeaders]);

  // Search
  const handleSearch = () => {
    if (!fromDate || !toDate) {
      toast.warning("Please Select From Date and To Date");
      return;
    }

    const queryParams = new URLSearchParams({
      branchId: selectedBranchId || 0,
      fromDate,
      toDate,
      vehicleCode: vehicleCode && vehicleCode !== "" ? vehicleCode : "%",
    });

    axios
      .get(`${BASE}/hsd_list?${queryParams.toString()}`, {
        headers: commonHeaders,
      })
      .then((res) => {
        const records = res.data?.data || [];
        setData(records);
        if (records.length === 0) toast.info("No records found.");
      })
      .catch((err) => {
        console.error("HSD list fetch failed", err);
        toast.error("Failed to fetch HSD records");
      });
  };

  // Clear Filters
  const clearFilters = () => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);

    setFromDate(toInputDate(d));
    setToDate(toInputDate(new Date()));
    setVehicleCode("");
    setSelectedBranchId(parseInt(storedBranchId, 10) || 0);
    setData([]);
  };

  const tdClass = `px-4 py-3 whitespace-nowrap text-sm ${
    isDark ? "text-gray-300" : "text-gray-800"
  }`;

  return (
    <LayoutWrapper>
      <HeaderBar title="HSD HOUR UPDATION LISTING" />
      <ToastContainer />

      <div
        className={`p-6 mt-6 rounded-2xl shadow-xl border ${
          isDark
            ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
            : `bg-white ${currentSkin.glass} text-black`
        }`}
      >
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-4">
            <h1
              className={`text-2xl font-bold ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              HSD Hour Updation
            </h1>
            <span
              className={`px-3 py-1 rounded-full text-sm font-medium ${
                isDark
                  ? "bg-blue-900 text-blue-100"
                  : "bg-blue-100 text-blue-800"
              }`}
            >
              {normalizedData.length} Records
            </span>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                isDark
                  ? "border-gray-600 text-white hover:bg-gray-700" +
                    (showFilters ? " bg-gray-700" : "")
                  : "border-gray-300 text-gray-700 hover:bg-gray-100" +
                    (showFilters ? " bg-gray-100" : "")
              }`}
            >
              <FaFilter className="w-4 h-4" />
              {showFilters ? "Hide Filters" : "Show Filters"}
            </button>

            <button
              onClick={() => navigate("/inventory/VehicleHsdHourUpdation")}
              className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded transition-colors`}
            >
              <FaPlus className="w-4 h-4" />
              Add New
            </button>
          </div>
        </div>

        {showFilters && (
          <div
            className={`rounded-xl p-6 mb-6 border ${
              isDark
                ? "bg-gray-800 border-gray-700"
                : "bg-gray-50 border-gray-200"
            }`}
          >
            <h3
              className={`text-lg font-semibold mb-4 ${
                isDark ? "text-white" : "text-gray-900"
              }`}
            >
              Search Filters
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  From Date *
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  To Date *
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                />
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Branch
                </label>
                <select
                  value={selectedBranchId}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSelectedBranchId(
                      val === "" || val === "0" ? 0 : parseInt(val, 10)
                    );
                    setVehicleCode("");
                  }}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  }`}
                >
                  <option value="0">-- Select Branch --</option>
                  {branchList.map((b, idx) => (
                    <option key={idx} value={String(b.fld_Branchid)}>
                      {b.fld_BranchName}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label
                  className={`block text-sm font-medium mb-2 ${
                    isDark ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  Vehicle Code
                </label>
                <select
                  value={vehicleCode}
                  onChange={(e) => setVehicleCode(e.target.value)}
                  disabled={!selectedBranchId || selectedBranchId === 0}
                  className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
                    isDark
                      ? "bg-gray-700 border-gray-600 text-white"
                      : "bg-white border-gray-300 text-gray-900"
                  } ${
                    !selectedBranchId || selectedBranchId === 0
                      ? "opacity-50 cursor-not-allowed"
                      : ""
                  }`}
                >
                  <option value="">-- Select Vehicle --</option>
                  {vehicleOptions.map((v, idx) => (
                    <option key={idx} value={v.fld_vehichlecode}>
                      {v.fld_vehichlecode}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div
              className={`flex justify-between items-center mt-6 pt-4 border-t ${
                isDark ? "border-gray-600" : "border-gray-200"
              }`}
            >
              <button
                onClick={clearFilters}
                className={`px-4 py-2 font-medium ${
                  isDark
                    ? "text-gray-300 hover:text-white"
                    : "text-gray-600 hover:text-gray-800"
                }`}
              >
                Clear Filters
              </button>

              <button
                onClick={handleSearch}
                className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded transition-colors`}
              >
                <FaSearch className="w-4 h-4" />
                Search Records
              </button>
            </div>
          </div>
        )}

        {normalizedData.length > 0 ? (
          <div
            className={`rounded-xl border overflow-hidden ${
              isDark
                ? "bg-gray-800 border-gray-700"
                : "bg-white border-gray-200"
            }`}
          >
            <div
              className={`p-4 border-b ${
                isDark ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <h3
                className={`text-lg font-semibold ${
                  isDark ? "text-white" : "text-gray-900"
                }`}
              >
                HSD Hour Updation Records ({normalizedData.length})
              </h3>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className={`bg-${baseColor}-${baseShade}`}>
                  <tr>
                    {[
                      "S.No",
                      "SI Date",
                      "Updated Date",
                      "Qty",
                      "Vehicle Code : Name",
                      "Pre Starting Hr",
                      "Cur Starting Hr",
                      "Pre Closing Hr",
                      "Cur Closing Hr",
                      "Pre Starting KM",
                      "Cur Starting KM",
                      "Pre Closing KM",
                      "Cur Closing KM",
                      "User Name",
                    ].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-xs font-medium text-white uppercase tracking-wider whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody
                  className={`divide-y ${
                    isDark
                      ? "divide-gray-700 bg-gray-800"
                      : "divide-gray-200 bg-white"
                  }`}
                >
                  {normalizedData.map((item, idx) => (
                    <tr
                      key={item.fld_HSDHrUpdationId ?? idx}
                      className={`transition-colors ${
                        idx % 2 === 0
                          ? isDark
                            ? "bg-gray-800 hover:bg-gray-700"
                            : "bg-white hover:bg-gray-50"
                          : isDark
                          ? "bg-gray-750 hover:bg-gray-700"
                          : "bg-gray-50 hover:bg-gray-100"
                      }`}
                    >
                      <td className={tdClass}>{idx + 1}</td>
                      <td className={tdClass}>{showVal(item._sidate)}</td>
                      <td className={tdClass}>{showVal(item._updated)}</td>
                      <td className={tdClass}>{showVal(item._qty)}</td>
                      <td className={`${tdClass} font-medium`}>
                        {showVal(item._vehicleDisplay)}
                      </td>

                      <td className={tdClass}>{showVal(item._preStartHr)}</td>
                      <td className={tdClass}>{showVal(item._curStartHr)}</td>
                      <td className={tdClass}>{showVal(item._preCloseHr)}</td>
                      <td className={tdClass}>{showVal(item._curCloseHr)}</td>

                      <td className={tdClass}>{showVal(item._preStartKm)}</td>
                      <td className={tdClass}>{showVal(item._curStartKm)}</td>
                      <td className={tdClass}>{showVal(item._preCloseKm)}</td>
                      <td className={tdClass}>{showVal(item._curCloseKm)}</td>

                      <td className={tdClass}>{showVal(item._user)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          showFilters && (
            <div
              className={`text-center py-12 ${
                isDark ? "text-gray-400" : "text-gray-500"
              }`}
            >
              <p>
                No records found. Please use the filters to search for HSD hour
                records.
              </p>
            </div>
          )
        )}
      </div>
    </LayoutWrapper>
  );
};

export default HsdHrUpdate;